import { X, Globe } from 'lucide-react';

interface CustomConfigurationsPageProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeClick?: () => void;
}

export function CustomConfigurationsPage({ isOpen, onClose, onUpgradeClick }: CustomConfigurationsPageProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[130] p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-[900px] max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-br from-purple-50 to-blue-50 border-b-2 border-purple-300 p-4 sm:p-5 rounded-t-2xl z-10">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-purple-100 active:bg-purple-200 rounded-lg transition-colors touch-manipulation"
            aria-label="Close"
          >
            <X className="size-4 text-gray-600" />
          </button>
          
          <div className="pr-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Integration Options</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Introduction */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-4 sm:p-5">
            {/* Description Text */}
            <div className="space-y-2">
              <p className="text-sm text-gray-700 leading-relaxed">
                Your ORA agent isn't limited to a small set of integrations — it can work with any service that has a usable web interface, meaning anything you can open in a browser, click, type into, or navigate. That makes the ecosystem extremely robust!
              </p>
              <p className="text-sm text-gray-700 leading-relaxed italic">
                Here's a comprehensive, platform‑agnostic list of categories and examples your agent can typically interact with.
              </p>
            </div>
          </div>

          {/* Integration Categories */}
          <div className="space-y-5">
            {/* Productivity & Office Suites */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Productivity & Office Suites</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Outlook.com</li>
                <li>Office.com (Word, Excel, PowerPoint, OneDrive)</li>
                <li>Google Workspace (Gmail, Calendar, Drive, Docs, Sheets)</li>
                <li>Zoho (Mail, Docs, CRM)</li>
                <li>Proton Mail (web UI)</li>
                <li>iCloud (web apps)</li>
              </ul>
            </div>

            {/* Cloud Storage & File Management */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Cloud Storage & File Management</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Dropbox</li>
                <li>Box</li>
                <li>Google Drive</li>
                <li>OneDrive</li>
                <li>SharePoint Online</li>
                <li>Egnyte</li>
                <li>pCloud</li>
                <li>Mega (web UI)</li>
              </ul>
            </div>

            {/* Scheduling & Booking Platforms */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Scheduling & Booking Platforms</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Calendly</li>
                <li>Google Calendar</li>
                <li>Outlook Calendar</li>
                <li>Doodle</li>
                <li>Acuity Scheduling</li>
                <li>Setmore</li>
                <li>Any web‑based appointment system</li>
                <li>Eventbrite (event creation/management)</li>
              </ul>
            </div>

            {/* Communication & Collaboration Tools */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Communication & Collaboration Tools</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Slack (web version)</li>
                <li>Microsoft Teams (web version)</li>
                <li>Zoom (web portal)</li>
                <li>Webex (web portal)</li>
                <li>Discord (web version)</li>
                <li>Mattermost</li>
                <li>Workplace by Meta</li>
              </ul>
            </div>

            {/* Project & Task Management */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Project & Task Management</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Asana</li>
                <li>Trello</li>
                <li>Monday.com</li>
                <li>ClickUp</li>
                <li>Jira</li>
                <li>Basecamp</li>
                <li>Notion</li>
                <li>Airtable</li>
                <li>Smartsheet</li>
                <li>Wrike</li>
              </ul>
            </div>

            {/* Forms, Workflows & Automation */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Forms, Workflows & Automation</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Google Forms</li>
                <li>Microsoft Forms</li>
                <li>Typeform</li>
                <li>Jotform</li>
                <li>Wufoo</li>
                <li>Formstack</li>
                <li>ServiceNow</li>
                <li>SAP SuccessFactors (web UI)</li>
                <li>Workday (web UI)</li>
                <li>Any custom form‑based workflow</li>
              </ul>
            </div>

            {/* CRM & Sales Platforms */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">CRM & Sales Platforms</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>HubSpot</li>
                <li>Salesforce (web UI)</li>
                <li>Zoho CRM</li>
                <li>Pipedrive</li>
                <li>Freshsales</li>
                <li>Copper CRM</li>
                <li>Keap</li>
                <li>Insightly</li>
              </ul>
            </div>

            {/* Document Signing & Legal */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Document Signing & Legal</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>DocuSign</li>
                <li>Adobe Sign</li>
                <li>PandaDoc</li>
                <li>HelloSign</li>
                <li>SignNow</li>
              </ul>
            </div>

            {/* E‑commerce & Payment Platforms */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">E‑commerce & Payment Platforms</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Shopify (admin portal)</li>
                <li>WooCommerce dashboards</li>
                <li>Stripe Dashboard</li>
                <li>PayPal Business</li>
                <li>Square Dashboard</li>
                <li>Etsy Shop Manager</li>
                <li>Amazon Seller Central</li>
              </ul>
            </div>

            {/* Knowledge & Content Platforms */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Knowledge & Content Platforms</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Confluence</li>
                <li>SharePoint Sites</li>
                <li>Notion</li>
                <li>Coda</li>
                <li>GitHub (web UI)</li>
                <li>GitLab (web UI)</li>
                <li>Medium</li>
                <li>WordPress Admin</li>
              </ul>
            </div>

            {/* HR, Recruiting & Talent Platforms */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">HR, Recruiting & Talent Platforms</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>LinkedIn (web UI)</li>
                <li>Indeed Employer</li>
                <li>Greenhouse</li>
                <li>Lever</li>
                <li>BambooHR</li>
                <li>Workday</li>
                <li>ADP (web UI)</li>
              </ul>
            </div>

            {/* Finance & Accounting Tools */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">Finance & Accounting Tools</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>QuickBooks Online</li>
                <li>Xero</li>
                <li>FreshBooks</li>
                <li>Wave</li>
                <li>Expensify</li>
                <li>Bill.com</li>
              </ul>
            </div>

            {/* IT & DevOps Tools */}
            <div>
              <h4 className="text-sm font-bold text-black mb-2">IT & DevOps Tools</h4>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Azure DevOps (web UI)</li>
                <li>GitHub</li>
                <li>GitLab</li>
                <li>Bitbucket</li>
                <li>Jenkins dashboards</li>
                <li>Grafana dashboards</li>
                <li>Datadog (web UI)</li>
              </ul>
            </div>

            {/* The Rule of Thumb */}
            <div className="pt-4 border-t-2 border-gray-300">
              <h4 className="text-sm font-bold text-black mb-2">The Rule of Thumb</h4>
              <p className="text-sm text-gray-700 mb-2">If it runs in a browser:</p>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside ml-4">
                <li>has buttons, menus, or forms</li>
                <li>doesn't block automation</li>
              </ul>
              <p className="text-sm font-semibold text-black mt-3">your agent can typically navigate and connect to it.</p>
            </div>
          </div>

          {/* Upgrade CTA */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-5 text-center">
            <h3 className="text-base font-bold text-black mb-2">Ready to customize your ORA experience?</h3>
            <p className="text-sm text-gray-700 mb-4">
              Upgrade to Create your persona Avatar Template or Solo Pro to unlock custom context configurations and integrations
            </p>
            <a
              href="https://agent.myora.now/?view=pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors text-sm inline-flex items-center justify-center no-underline hover:no-underline"
            >
              View Pricing Plans
            </a>
          </div>

          {/* Close Button */}
          <div className="flex justify-center pt-2">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-black rounded-lg font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}